import React from 'react';
import { Input } from '../../ui';
import { Clock, Calendar, Settings } from 'lucide-react';

interface BusinessHoursConfigProps {
  config: any;
  onChange: (key: string, value: any) => void;
}

const DAYS = [
  { value: 0, label: 'Domingo' },
  { value: 1, label: 'Segunda' },
  { value: 2, label: 'Terça' },
  { value: 3, label: 'Quarta' },
  { value: 4, label: 'Quinta' },
  { value: 5, label: 'Sexta' },
  { value: 6, label: 'Sábado' },
];

export const BusinessHoursConfig: React.FC<BusinessHoursConfigProps> = ({ config, onChange }) => {
  const workDays = config.workDays || [1, 2, 3, 4, 5]; // Default Mon-Fri
  const type = config.type || 'custom';

  const toggleDay = (dayValue: number) => {
    if (workDays.includes(dayValue)) {
      onChange('workDays', workDays.filter((d: number) => d !== dayValue));
    } else {
      onChange('workDays', [...workDays, dayValue].sort());
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700 leading-relaxed">
        <strong>Como funciona:</strong> Verifica se a mensagem foi recebida dentro do horário comercial configurado. Possui duas saídas: "Dentro do horário" e "Fora do horário", permitindo direcionar o fluxo de forma diferente (ex: atender ou enviar mensagem de ausência). Pode usar o horário do Bitrix24 ou configuração personalizada.
      </div>
      {/* Type Selector */}
      <div className="grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-lg">
        <button
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
            type === 'bitrix'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          }`}
          onClick={() => onChange('type', 'bitrix')}
        >
          Usar Bitrix24
        </button>
        <button
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
            type === 'custom'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          }`}
          onClick={() => onChange('type', 'custom')}
        >
          Personalizado
        </button>
      </div>

      {type === 'bitrix' ? (
        <div className="p-3 bg-blue-50 rounded-lg border border-blue-100 flex gap-3">
          <Settings className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <h4 className="text-xs font-medium text-blue-800">Sincronização Automática</h4>
            <p className="text-[10px] text-blue-600 leading-relaxed">
              O robô irá consultar as configurações de horário de trabalho do seu Bitrix24 (Configurações &gt; Horário de Trabalho ou Canal Aberto).
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Input
                label="Início"
                type="time"
                value={config.startTime || '09:00'}
                onChange={(e) => onChange('startTime', e.target.value)}
              />
            </div>
            <div>
              <Input
                label="Fim"
                type="time"
                value={config.endTime || '18:00'}
                onChange={(e) => onChange('endTime', e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-2">
              Dias de Funcionamento
            </label>
            <div className="grid grid-cols-2 gap-2">
              {DAYS.map((day) => (
                <button
                  key={day.value}
                  onClick={() => toggleDay(day.value)}
                  className={`
                    flex items-center gap-2 px-3 py-2 text-xs rounded border transition-colors
                    ${workDays.includes(day.value)
                      ? 'bg-blue-50 border-blue-200 text-blue-700 font-medium'
                      : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                    }
                  `}
                >
                  <Calendar className="w-3 h-3" />
                  {day.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">
              Fuso Horário
            </label>
            <div className="relative">
                <Clock className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <select
                    className="w-full pl-8 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    value={config.timezone || 'America/Sao_Paulo'}
                    onChange={(e) => onChange('timezone', e.target.value)}
                >
                    <option value="America/Sao_Paulo">Brasília (GMT-3)</option>
                    <option value="America/Manaus">Manaus (GMT-4)</option>
                    <option value="America/Rio_Branco">Rio Branco (GMT-5)</option>
                    <option value="America/Noronha">Fernando de Noronha (GMT-2)</option>
                    <option value="UTC">UTC (GMT+0)</option>
                </select>
            </div>
          </div>
        </>
      )}

      <div className="pt-2 border-t border-slate-100">
        <p className="text-[10px] text-slate-400 text-center">
            Saídas: <span className="text-green-600 font-medium">Aberto</span> e <span className="text-red-600 font-medium">Fechado</span>
        </p>
      </div>
    </div>
  );
};
